/**
 * Survey Frequency Storage Utilities
 *
 * Handles reading/writing survey frequency data to localForage (IndexedDB).
 */

import localforage from 'localforage'
import { SURVEY_FREQUENCY_STORAGE_KEY } from './surveyFrequencyConfig'
import type { SurveyFrequencyData } from './surveyFrequencyTypes'

// Configure localforage to use the same database as the rest of the app
localforage.config({
  name: 'rpp',
})

/**
 * Creates an empty/default survey frequency data structure
 */
export const createDefaultFrequencyData = (): SurveyFrequencyData => ({
  surveys: [],
})

/**
 * Clears all survey frequency data from storage
 * Useful for testing or resetting user state
 */
export const clearFrequencyData = async (): Promise<void> => {
  try {
    await localforage.removeItem(SURVEY_FREQUENCY_STORAGE_KEY)
  } catch {
    console.warn('[SurveyFrequency] Failed to clear frequency data from storage')
  }
}

/**
 * Reads survey frequency data from storage
 * Returns default data if nothing is stored or if data is corrupted
 */
export const readFrequencyData = async (): Promise<SurveyFrequencyData> => {
  try {
    const stored = await localforage.getItem<SurveyFrequencyData>(SURVEY_FREQUENCY_STORAGE_KEY)
    if (!stored) {
      return createDefaultFrequencyData()
    }

    // Basic validation - ensure required structure exists
    if (!Array.isArray(stored.surveys)) {
      await clearFrequencyData()
      return createDefaultFrequencyData()
    }

    return stored
  } catch {
    // If reading fails, clear the corrupted data and return defaults
    await clearFrequencyData()
    return createDefaultFrequencyData()
  }
}

/**
 * Writes survey frequency data to storage
 */
export const writeFrequencyData = async (data: SurveyFrequencyData): Promise<void> => {
  try {
    await localforage.setItem(SURVEY_FREQUENCY_STORAGE_KEY, data)
  } catch {
    // Silently fail if storage is full or unavailable
    // Survey frequency is not critical enough to break the app
    console.warn('[SurveyFrequency] Failed to write frequency data to storage')
  }
}
